// Static map decals (stains, floor markings, numerals): every decal of a map becomes one quad of a
// single merged mesh that samples the shared decal atlas, so the whole set costs one draw call, one
// texture and one shader program. Decals lie a hair above the surface (small offset plus polygon
// offset), are alpha blended without writing depth, and receive the sun's shadows like the world.

import * as THREE from "three";
import type { BakedLight } from "./bakedLight";
import type { MapDecal, MapVec3 } from "./mapTypes";
import { decalUv } from "./materials/decalKinds";
import { getDecalAtlas } from "./materials/textures";

/** Distance (m) the quad floats above the surface. */
const LIFT = 0.012;

export interface DecalGeometry {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  /** RGBA per vertex: tint (linear) and alpha. */
  colors: Float32Array;
  indices: Uint16Array;
  /** Decals that made it into the mesh (unknown kinds are skipped). */
  count: number;
}

function normalize(v: MapVec3): MapVec3 {
  const l = Math.hypot(v[0], v[1], v[2]);
  return [v[0] / l, v[1] / l, v[2] / l];
}

function cross(a: MapVec3, b: MapVec3): MapVec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

/**
 * Axes of a decal's picture (the same definition as tools/mapgen geom.ts `decalAxes`): "up" is world
 * +Y on walls and -Z on floors and ceilings, "right" = up x normal, both turned `rotDeg`
 * counter-clockwise about the normal.
 */
export function decalAxes(normal: MapVec3, rotDeg: number): { right: MapVec3; up: MapVec3 } {
  const n = normalize(normal);
  const hint: MapVec3 = Math.abs(n[1]) > 0.9 ? [0, 0, -1] : [0, 1, 0];
  const d = hint[0] * n[0] + hint[1] * n[1] + hint[2] * n[2];
  const up0 = normalize([hint[0] - n[0] * d, hint[1] - n[1] * d, hint[2] - n[2] * d]);
  const right0 = normalize(cross(up0, n));
  const r = (rotDeg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return {
    right: [right0[0] * c + up0[0] * s, right0[1] * c + up0[1] * s, right0[2] * c + up0[2] * s],
    up: [up0[0] * c - right0[0] * s, up0[1] * c - right0[1] * s, up0[2] * c - right0[2] * s],
  };
}

/** Merges the decals into one quad each. Pure data: no THREE objects besides colour conversion. */
export function buildDecalGeometry(decals: readonly MapDecal[]): DecalGeometry {
  const quads = decals.filter((d) => decalUv(d.kind) !== null);
  const positions = new Float32Array(quads.length * 12);
  const normals = new Float32Array(quads.length * 12);
  const uvs = new Float32Array(quads.length * 8);
  const colors = new Float32Array(quads.length * 16);
  const indices = new Uint16Array(quads.length * 6);
  const tint = new THREE.Color();
  quads.forEach((d, q) => {
    const uv = decalUv(d.kind)!;
    const n = normalize(d.normal);
    const { right, up } = decalAxes(n, d.rot ?? 0);
    const side: MapVec3 = d.mirror === true ? [-right[0], -right[1], -right[2]] : right;
    const hw = d.size[0] / 2;
    const hh = d.size[1] / 2;
    const base: MapVec3 = [d.pos[0] + n[0] * LIFT, d.pos[1] + n[1] * LIFT, d.pos[2] + n[2] * LIFT];
    // Corners: bottom-left, bottom-right, top-right, top-left of the picture.
    const corners: ReadonlyArray<readonly [number, number, number, number]> = [
      [-1, -1, uv.u0, uv.v0],
      [1, -1, uv.u1, uv.v0],
      [1, 1, uv.u1, uv.v1],
      [-1, 1, uv.u0, uv.v1],
    ];
    tint.set(d.color ?? "#ffffff");
    corners.forEach(([sx, sy, u, v], k) => {
      const vi = q * 4 + k;
      for (let a = 0; a < 3; a++) {
        positions[vi * 3 + a] = base[a] + side[a] * sx * hw + up[a] * sy * hh;
        normals[vi * 3 + a] = n[a];
      }
      uvs[vi * 2] = u;
      uvs[vi * 2 + 1] = v;
      colors.set([tint.r, tint.g, tint.b, d.alpha ?? 1], vi * 4);
    });
    // Counter-clockwise seen from the normal side; a mirrored picture has its right axis flipped.
    const sideXUp = cross(side, up);
    const faces = sideXUp[0] * n[0] + sideXUp[1] * n[1] + sideXUp[2] * n[2] > 0;
    const o = q * 4;
    indices.set(faces ? [o, o + 1, o + 2, o, o + 2, o + 3] : [o, o + 2, o + 1, o, o + 3, o + 2], q * 6);
  });
  return { positions, normals, uvs, colors, indices, count: quads.length };
}

export interface StaticDecals {
  mesh: THREE.Mesh;
  /** Frees the geometry and material (the atlas texture is shared and stays cached). */
  dispose(): void;
}

/** One mesh for all decals of a map, or null when there are none. */
export function buildStaticDecals(decals: readonly MapDecal[] | undefined, baked: BakedLight | null): StaticDecals | null {
  if (decals === undefined || decals.length === 0) return null;
  const data = buildDecalGeometry(decals);
  if (data.count === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(data.positions, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(data.normals, 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(data.uvs, 2));
  geometry.setAttribute("color", new THREE.BufferAttribute(data.colors, 4));
  geometry.setIndex(new THREE.BufferAttribute(data.indices, 1));
  geometry.computeBoundingSphere();
  const material = new THREE.MeshStandardMaterial({
    map: getDecalAtlas(),
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    roughness: 0.9,
    metalness: 0,
  });
  // Lit like the surfaces under them; no grime response (the stains are the dirt).
  baked?.patch(material, 0);
  material.name = "static-decals";
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = "decals";
  mesh.matrixAutoUpdate = false;
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  // Before the glass of the world, so windows and rails draw over the marks behind them.
  mesh.renderOrder = -1;
  return {
    mesh,
    dispose(): void {
      mesh.removeFromParent();
      geometry.dispose();
      material.dispose();
    },
  };
}
